/**
 * App shell (PHASE1 §7.1). Desktop shows a persistent left `Sidebar` (≥md);
 * mobile shows a `BottomNav` with a "Mehr" sheet for overflow items. The routed
 * page renders in the `<Outlet>`.
 */
import { Outlet, useLocation } from 'react-router';
import type { ComponentType } from 'react';
import {
  Inbox,
  Receipt,
  Landmark,
  Tags,
  Filter,
  Upload,
  Wallet,
  BarChart3,
  ArrowLeftRight,
  Download,
  FileSignature,
  ShieldAlert,
  type LucideProps,
} from 'lucide-react';
import { Sidebar } from './Sidebar.tsx';
import { BottomNav } from './BottomNav.tsx';
import { OfflineBanner } from '../OfflineBanner.tsx';
import { InstallPrompt } from '../InstallPrompt.tsx';
import type { CurrentUser } from '../AuthGate.tsx';

export interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<LucideProps>;
}

/** Canonical nav order (§7.1). The first `PRIMARY_NAV_COUNT` show in the mobile
 * bottom bar; the rest live in the "Mehr" sheet. */
export const NAV_ITEMS: NavItem[] = [
  { to: '/inbox', label: 'Inbox', icon: Inbox },
  { to: '/transaktionen', label: 'Transaktionen', icon: Receipt },
  { to: '/budget', label: 'Budget', icon: Wallet },
  { to: '/berichte', label: 'Berichte', icon: BarChart3 },
  { to: '/konten', label: 'Konten', icon: Landmark },
  { to: '/kategorien', label: 'Kategorien', icon: Tags },
  { to: '/regeln', label: 'Regeln', icon: Filter },
  { to: '/vertraege', label: 'Verträge', icon: FileSignature },
  { to: '/import', label: 'Import', icon: Upload },
  { to: '/bankverbindungen', label: 'Bankverbindungen', icon: Landmark },
  { to: '/transfers', label: 'Transfers', icon: ArrowLeftRight },
  { to: '/daten-export', label: 'Daten-Export', icon: Download },
];

export const PRIMARY_NAV_COUNT = 4;

/** Admin-only entry — appended per user, never part of the static list. */
const ADMIN_NAV_ITEM: NavItem = { to: '/admin', label: 'Admin', icon: ShieldAlert };

/** The nav list for a concrete user: the canonical items, plus Admin when
 * `is_admin` (server-computed from ADMIN_EMAIL — the page 403s regardless). */
export function navItemsFor(user: CurrentUser): NavItem[] {
  return user.is_admin ? [...NAV_ITEMS, ADMIN_NAV_ITEM] : NAV_ITEMS;
}

export function AppShell() {
  const { pathname } = useLocation();
  return (
    <div className="min-h-screen bg-white text-slate-900 dark:bg-slate-950 dark:text-slate-100">
      <div className="flex w-full">
        <Sidebar />
        <main className="min-h-screen w-full min-w-0 flex-1 pb-20 md:pb-0">
          <OfflineBanner />
          {/* Route-change content fade (DESIGN.md §4.1) — keyed on the pathname
           * so only the routed page animates, never the shell chrome. Fade-only:
           * a transform here would make this div the sticky PageHeader's
           * containing block mid-animation and unpin the topbar. */}
          <div key={pathname} className="animate-[page-enter-fade_0.25s_ease-out]">
            <Outlet />
          </div>
        </main>
      </div>
      <BottomNav />
      <InstallPrompt />
    </div>
  );
}
